/**
 * Guards against paying for (and displaying) STT output when the user didn't
 * actually say anything. Whisper hallucinates YouTube-outro phrases
 * ("subscribe to the channel", "thanks for watching") on silent or near-silent
 * Arabic audio, so we check twice:
 *   1. hasSpeechContent — client-side RMS check on the recorded blob, BEFORE
 *      the STT call (free, instant, no network).
 *   2. isWhisperHallucination — blocklist of known hallucinated phrases,
 *      AFTER transcription.
 * Shared by Free Chat / Role Play (ConversationPanel) and Practice Calls.
 */

/**
 * Returns true if the audio blob contains audible speech (RMS > threshold).
 * Decodes client-side via Web Audio API — free, instant, no network call.
 */
export const hasSpeechContent = async (blob, threshold = 0.02) => {
  try {
    const arrayBuffer = await blob.arrayBuffer();
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
    audioCtx.close();
    for (let ch = 0; ch < audioBuffer.numberOfChannels; ch++) {
      const data = audioBuffer.getChannelData(ch);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
      if (Math.sqrt(sum / data.length) > threshold) return true;
    }
    return false;
  } catch {
    return true; // if decode fails, let the server decide
  }
};

// Known Whisper hallucinations for near-silent Arabic audio
const WHISPER_HALLUCINATIONS = new Set([
  "اشتركوا في القناة",
  "اشترك في القناة",
  "للمشتركين الجدد",
  "شكراً للمشاهدة",
  "شكرا للمشاهدة",
  "شكراً على المشاهدة",
  "شكرا على المشاهدة",
  "تابعونا على",
  "لا إله إلا الله",
  "subscribe",
  "thanks for watching",
  "thank you for watching",
  "please subscribe",
]);

// Strip harakat + tatweel so "شُكْرًا" still matches the bare phrase.
const norm = (s) =>
  (s || "").trim().toLowerCase().replace(/[\u064B-\u065F\u0670\u0640]/g, "");

export const isWhisperHallucination = (text) => {
  const normalised = norm(text);
  for (const phrase of WHISPER_HALLUCINATIONS) {
    if (normalised.includes(norm(phrase))) return true;
  }
  return false;
};
